/**
 * AI Workspace Setup Confirmation Dialog
 *
 * Consent step shown before an AI workspace is created. It exists because
 * clicking "AI Workspace" used to start exporting schema DDL immediately with
 * no explanation (support issue: the reporter was alarmed). The dialog lets the
 * user pick the target database and see an honest account of what gets exported
 * before anything runs. Gated behind `aiWorkspace.confirmBeforeSetup` (default
 * on); a "Don't ask me again" checkbox flips it off on confirm.
 */
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useAppStore } from '@/store/appStore';
import { useDatabaseList } from '@/features/database-objects/hooks/useDatabaseList';
import { coerceDatabaseToConnectionLimit } from '@/features/connections/utils/connectionDatabaseLimit';
import type { ActiveConnection } from '../../connections/models/connectionTypes';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

/** Verbatim consent copy — do not paraphrase (see task D1 brief). */
const AI_CONSENT_BODY =
  'Jam SQL Studio will set up an AI workspace for this connection. It exports schema DDL — tables, views, procedures, functions. No table data is read or exported, and nothing is executed against your database. Files are written to a local workspace folder.';

type AiWorkspaceSetupConfirmDialogProps = {
  open: boolean;
  connection: ActiveConnection;
  /** Current target database, used to seed the Select. */
  initialDatabase?: string;
  onConfirm: (database: string | undefined) => void;
  onCancel: () => void;
};

export function AiWorkspaceSetupConfirmDialog({
  open,
  connection,
  initialDatabase,
  onConfirm,
  onCancel,
}: AiWorkspaceSetupConfirmDialogProps) {
  const { track } = useAnalytics();
  const setAiTerminalSettings = useAppStore((s) => s.setAiTerminalSettings);

  // V2 — when the connection is limited, an off-limit `initialDatabase` (e.g.
  // seeded from a pinned off-limit database node) must never be preselected or
  // offered. Coerce the seed to the limited database up front; unlimited
  // connections keep `initialDatabase` untouched.
  const effectiveInitialDatabase = coerceDatabaseToConnectionLimit(
    connection.connection,
    initialDatabase,
  );

  const [selectedDatabase, setSelectedDatabase] = useState<string | undefined>(
    effectiveInitialDatabase,
  );
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const engine = connection.connection.engine;
  const alias = safeConnectionName(connection.connection);

  // CP2-WAVE-D-FLAG (binding): list through the central `getDatabases` service
  // WITHOUT `ignoreConnectionLimit`, so a limited connection shows only its
  // limited list. `useDatabaseList` calls it with exactly `(connectionId,
  // engine)` and reads the SHARED per-connection slot the Object Explorer
  // already fills, so the dialog no longer re-lists rows the app has. It was
  // already Slice-5b correct (`[]` unless `state === 'ok'`); the hook derives
  // that centrally now.
  const { databases } = useDatabaseList({ connectionId: connection.id, engine });

  // Merge the seeded database into the options so its SelectItem exists even
  // before (or if) the listing resolves — otherwise the seeded value shows blank.
  const databaseOptions = useMemo(() => {
    const merged: string[] = [];
    const seen = new Set<string>();
    for (const db of [effectiveInitialDatabase, ...databases]) {
      const trimmed = db?.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      merged.push(trimmed);
    }
    return merged;
  }, [effectiveInitialDatabase, databases]);

  const chosenDatabase = selectedDatabase?.trim() || undefined;

  const handleConfirm = () => {
    if (dontAskAgain) {
      setAiTerminalSettings({ aiWorkspace: { confirmBeforeSetup: false } });
    }
    track('feature:ai_workspace_consent', { accepted: true, dont_ask_again: dontAskAgain });
    onConfirm(chosenDatabase);
  };

  const handleCancel = () => {
    track('feature:ai_workspace_consent', { accepted: false, dont_ask_again: dontAskAgain });
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel();
      }}
    >
      <DialogContent className="max-w-lg" data-testid="ai-workspace-consent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Set up AI workspace
          </DialogTitle>
          <DialogDescription>
            Connection <strong>{alias}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ai-consent-database">Database</Label>
            <Select value={selectedDatabase} onValueChange={setSelectedDatabase}>
              <SelectTrigger id="ai-consent-database" data-testid="ai-consent-database-trigger">
                <SelectValue placeholder="Server (no database)" />
              </SelectTrigger>
              <SelectContent>
                {databaseOptions.map((db) => (
                  <SelectItem key={db} value={db}>
                    {db}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p data-testid="ai-consent-body" className="text-sm text-muted-foreground">
            {AI_CONSENT_BODY}
          </p>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox
              checked={dontAskAgain}
              onCheckedChange={(checked) => setDontAskAgain(checked === true)}
              data-testid="ai-consent-dont-ask"
            />
            Don't ask me again
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} data-testid="ai-consent-cancel">
            Cancel
          </Button>
          <Button onClick={handleConfirm} data-testid="ai-consent-confirm">
            Set up workspace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
